<template>
	<div class="min-h-screen bg-white font-sans">
		<!-- Header -->
		<header
			class="bg-white border-b border-gray-200 shadow-sm py-6 relative"
		>
			<div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8 text-center">
				<h1
					class="text-2xl font-extrabold text-gray-900 mb-3 leading-tight tracking-tight"
				>
					Quotation Preview
				</h1>
				<p
					class="text-sm text-gray-600 leading-relaxed max-w-2xl mx-auto"
				>
					Review and manage your insurance quotation recommendations
				</p>
			</div>
		</header>

		<!-- Main Content -->
		<main
			class="py-6"
			v-for="quotation in selectQuotationResults"
			:key="quotation.id"
		>
			<div class="px-4 sm:px-6 lg:px-8">
				<div class="flex flex-col lg:flex-row gap-6">
					<!-- Aside Section - Quotation Summary -->
					<aside class="w-full lg:w-80 flex-shrink-0">
						<section
							class="bg-white rounded-xl shadow-md border border-gray-200 p-5"
						>
							<div class="mb-5 flex flex-col gap-3">
								<h2 class="text-lg font-bold text-gray-900 m-0">
									Quotation Summary
								</h2>
								<div
									class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-semibold capitalize w-fit"
									:class="{
										'bg-amber-100 text-amber-900':
											getStatusClass(quotation.status) ===
											'status-pending',
										'bg-emerald-100 text-emerald-900':
											getStatusClass(quotation.status) ===
											'status-approved',
										'bg-red-100 text-red-900':
											getStatusClass(quotation.status) ===
											'status-rejected',
										'bg-blue-100 text-blue-900':
											getStatusClass(quotation.status) ===
											'status-processing',
										'bg-gray-100 text-gray-700':
											getStatusClass(quotation.status) ===
											'status-default',
									}"
								>
									<i
										:class="getStatusIcon(quotation.status)"
										class="text-[11px]"
									></i>
									{{ quotation.status }}
								</div>
							</div>

							<div class="space-y-5">
								<div class="space-y-4">
									<div>
										<div
											class="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-1.5"
										>
											<i
												class="fas fa-calendar-alt text-primary text-[11px]"
											></i>
											Expiry Date
										</div>
										<div
											class="text-sm text-gray-900 font-medium leading-snug"
										>
											{{ formatDate(quotation.expiry) }}
										</div>
									</div>
									<div>
										<div
											class="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-1.5"
										>
											<i
												class="fas fa-user-tie text-primary text-[11px]"
											></i>
											Intermediary
										</div>
										<!-- <div class="detail-value" v-for="kyc in quotation.intermediary.kyc">
                                            <p>{{ kyc.intermediary_names}}</p>

                                        </div> -->
									</div>
								</div>

								<div
									v-if="quotation.remarks"
									class="p-3.5 bg-gray-50 rounded-lg border-l-4 border-primary"
								>
									<div
										class="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-1.5"
									>
										<i
											class="fas fa-comment-alt text-primary"
										></i>
										Remarks
									</div>
									<div
										class="text-xs text-gray-700 leading-relaxed m-0"
									>
										{{ quotation.remarks }}
									</div>
								</div>
							</div>

							<button
								@click="rejectQuotationReview"
								class="w-full mt-5 px-4 py-2.5 border-2 border-accent bg-white text-accent font-semibold text-[13px] rounded-lg transition-all duration-200 hover:bg-accent hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
							>
								<i class="fas fa-times mr-1.5"></i>
								Reject Quotation
							</button>
						</section>
					</aside>

					<!-- Main Content Area - Recommendations -->
					<div class="flex-1 min-w-0">
						<section>
							<div class="mb-4">
								<h2
									class="text-xl font-bold text-gray-900 mb-2 m-0"
								>
									Recommended Products
								</h2>
								<p
									class="text-sm text-gray-600 m-0 leading-relaxed"
								>
									Our experts have curated these insurance
									options based on your profile and
									requirements
								</p>
							</div>

							<div
								class="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 max-w-sm lg:max-w-none mx-auto"
							>
								<article
									v-for="(
										recommendation, index
									) in quotation.reccommendations"
									:key="recommendation.id"
									class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/50 flex flex-col aspect-square lg:aspect-auto"
								>
									<header class="p-3 lg:p-4">
										<div
											class="flex lg:flex-row flex-col items-center lg:items-start gap-2.5 lg:gap-3"
										>
											<div
												class="w-14 h-14 lg:w-12 lg:h-12 bg-primary rounded-xl flex items-center justify-center text-white text-base flex-shrink-0 shadow-sm"
											>
												<img
													:src="
														recommendation.product
															.productLogo
													"
													:alt="`${recommendation.product.productName} Logo`"
													class="w-full h-full object-cover bg-white rounded-xl"
												/>
											</div>
											<div
												class="flex-1 min-w-0 text-center lg:text-left w-full"
											>
												<h3
													class="text-sm lg:text-base font-bold text-gray-900 mb-2 leading-tight break-words line-clamp-2"
												>
													{{
														recommendation.product
															.productName
													}}
												</h3>
												<div
													class="flex items-center justify-center lg:justify-start gap-1"
												>
													<div class="flex gap-0.5">
														<i
															v-for="n in recommendation.reccommendationLevel"
															:key="'filled-' + n"
															class="fas fa-star text-sm text-yellow-400"
														></i>
														<i
															v-for="n in 5 -
															recommendation.reccommendationLevel"
															:key="'empty-' + n"
															class="fas fa-star text-sm text-gray-300"
														></i>
													</div>
													<span
														class="hidden lg:inline text-xs text-primary font-semibold ml-1"
													>
														{{
															getLevelText(
																recommendation.reccommendationLevel,
															)
														}}
													</span>
												</div>
											</div>
										</div>
									</header>

									<div
										class="px-3 lg:px-4 pb-3 lg:pb-4 flex-grow flex flex-col justify-center"
									>
										<div
											class="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 text-center border border-gray-200/80 shadow-inner"
										>
											<div
												class="text-[10px] lg:text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5"
											>
												Premium
											</div>
											<div
												class="text-xl lg:text-2xl font-black text-primary leading-none"
											>
												<!-- {{ formatCurrency(quotation.premiumAmount, recommendation.product.supportedCurrency[0]) }} -->
												{{
													recommendation.premiumAmount
												}}
											</div>
										</div>

										<div
											v-if="recommendation.remarks"
											class="hidden lg:block mt-3"
										>
											<div
												class="text-xs font-semibold text-gray-700 mb-1.5"
											>
												Why we recommend this:
											</div>
											<p
												class="text-xs text-gray-600 leading-relaxed"
											>
												{{ recommendation.remarks }}
											</p>
										</div>
									</div>

									<footer
										class="px-3 lg:px-4 pb-3 lg:pb-4 mt-auto flex flex-col lg:flex-row gap-2 lg:gap-3"
									>
										<button
											@click="
												acceptReccomendationReview(
													recommendation,
												)
											"
											class="w-full order-1 lg:order-2 px-4 py-2.5 lg:py-3 rounded-lg font-bold text-xs lg:text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-primary border-2 border-primary text-white hover:bg-primary/90 hover:border-primary/90 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 active:translate-y-0"
										>
											<i class="fas fa-check text-xs"></i>
											<span>Accept Plan</span>
										</button>
										<button
											@click="
												viewRecommendation(
													recommendation,
												)
											"
											class="w-full order-2 lg:order-1 px-4 py-2 lg:py-2.5 rounded-lg font-semibold text-xs lg:text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-white border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary hover:bg-gray-50 hover:-translate-y-0.5 active:translate-y-0"
										>
											<i class="fas fa-eye text-xs"></i>
											<span class="hidden lg:inline"
												>View Details</span
											>
											<span class="lg:hidden"
												>Details</span
											>
										</button>
									</footer>
								</article>
							</div>
						</section>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import {
	AcceptReccommendation,
	CompleteQuotation,
	GetQuotationDetails,
	RejectQuotation,
} from '../../api/insurance';

export default {
	name: 'QuotationPreview',

	computed: {
		...mapGetters('quotation', ['selectQuotationResults']),
		...mapGetters('auth', ['isAuthenticated']),
	},
	created() {
		console.log('[QuotationPreview] created hook fired.');
		console.log(
			'[QuotationPreview] Route params on create:',
			this.$route.params,
		);
	},
	methods: {
		...mapActions('quotation', [
			'setQuotationResults',
			'setQuotationData',
			'setSelectedQuotationReview',
			'setSystemQuotationRef',
			'setInvoiceRef',
		]),
		...mapActions('auth', ['setPurchaseType']),
		...mapActions('risk', ['setKycData']),
		...mapActions('product', ['setProduct', 'setProductCharges']),
		...mapActions('quotation', ['setCalculatedQuotation']),

		async fetchQuotationsReview() {
			let data = {
				quotationRequestRef: this.$route.params.id,
			};
			try {
				const res = await GetQuotationDetails(data);
				if (res.status) {
					this.setQuotationResults(res.data);
					this.setProductCharges(res.data.product.productCharges);
					this.quotationData = res.data;
				}
			} catch (error) {}
		},
		async calculateCharegAmount(charge) {},
		async rejectQuotationReview() {
			const data = {
				quotationRequestRef: this.$route.params.id,
				status: 'REJECTED_BY_CLIENT',
			};
			try {
				const res = await RejectQuotation(data);
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Quotation Rejected',
						message: 'The quotation has been rejected',
						duration: 3000,
					});
				}
			} catch (err) {}
		},
		async acceptReccomendationReview(recommendation) {
			try {
				const payload = {
					reccommendationSystemRef:
						recommendation.reccommendationSystemRef,
				};

				const response = await AcceptReccommendation(payload);
				this.setCalculatedQuotation();

				if (!response.status) {
					throw new Error('Recommendation acceptance failed');
				}

				const systemQuotationRef =
					response.data.value._value.systemQuotationRef || null;

				// Set Vuex / state data
				this.setSelectedQuotationReview(recommendation);
				this.setSystemQuotationRef(systemQuotationRef);
				this.setProduct(recommendation.product);
				this.setKycData(recommendation.clientDetails);

				Event.$emit('notifications:show', {
					type: 'success',
					title: 'Quotation Accepted',
					message:
						'The Recommendation has been accepted successfully',
					duration: 3000,
				});

				if (this.isAuthenticated) {
					const ref = { systemQuotationRef };

					const quotationData = {
						coverType: 'comprehensive',
						period: recommendation.coverDetails.period || '',
						totalPremium: recommendation.premiumAmount,
						vehicleValue: recommendation.riskValue,
					};

					const res = await CompleteQuotation(ref);

					if (res.status) {
						this.setQuotationData(quotationData);
						this.setInvoiceRef(res.data.invoiceSystemRef);
						this.$router.push(
							'/motor/payment-confirmation/payment',
						);
					}
				} else {
					this.setPurchaseType('recommendation');
					this.setCalculatedQuotation({
						totalPremium: recommendation.premiumAmount,
					});
					this.$router.push('/insurance/auth');
				}
			} catch (err) {
				const errorMessage =
					err.response.data.errors ||
					err.message ||
					'Failed to complete quotation';

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Quotation Error',
					message: `${errorMessage}. Please try again`,
					duration: 3000,
				});
			}
		},

		formatDate(dateString) {
			if (!dateString) return 'N/A';
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			});
		},

		formatCurrency(amount, currency = 'USD') {
			if (!amount) return 'N/A';
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency,
			}).format(amount);
		},

		getStatusClass(status) {
			const statusMap = {
				pending: 'status-pending',
				ACCEPTED: 'status-approved',
				rejected: 'status-rejected',
				processing: 'status-processing',
			};
			return (
				statusMap[(status && status.toLowerCase()) || ''] ||
				'status-default'
			);
		},

		getStatusIcon(status) {
			const iconMap = {
				pending: 'fas fa-clock',
				ACCEPTED: 'fas fa-check-circle',
				rejected: 'fas fa-times-circle',
				processing: 'fas fa-spinner fa-spin',
			};
			return (
				iconMap[(status && status.toLowerCase()) || ''] ||
				'fas fa-info-circle'
			);
		},

		getLevelText(level) {
			const levelMap = {
				1: 'Basic Match',
				2: 'Good Match',
				3: 'Very Good Match',
				4: 'Excellent Match',
				5: 'Perfect Match',
			};
			return levelMap[level] || 'No Rating';
		},

		viewRecommendation(recommendation) {
			//   console.log(`[QuotationPreview] Viewing recommendation ID: ${id}`);
			this.setSelectedQuotationReview(recommendation);
			this.$router.push({
				name: 'QuotationDetails',
				params: {
					qid: this.$route.params.id,
					id: recommendation.id,
				},
			});
		},

		declineRecommendation() {
			console.log('[QuotationPreview] Quotation declined.');
			alert('Quotation declined.');
		},
	},
	mounted() {
		console.log(
			'[QuotationPreview] mounted hook fired.',
			this.quotationData,
		);
		console.log(
			'[QuotationPreview] Route params on mount:',
			this.$route.params,
		);
		this.fetchQuotationsReview();
		if (this.$route.params.id) {
			console.log(
				`[QuotationPreview] Loaded with ID: ${this.$route.params.id}`,
			);
		}
	},
};
</script>
